import { useState, useEffect, useId } from "react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ConfirmDialogTitle } from "@/components/ui/ConfirmDialog/ConfirmDialogTitle";
import { ConfirmDialogBody } from "@/components/ui/ConfirmDialog/ConfirmDialogBody";
import { ConfirmDialogActions } from "@/components/ui/ConfirmDialog/ConfirmDialogActions";
import { Input } from "@/components/ui/primitives/Input";
import { LucideIconPicker } from "@/components/documents/LucideIconPicker";
import type { Id } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

interface CategoryFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  initialData?: {
    _id: Id<"documentCategories">;
    name: string;
    icon: string;
  };
  onSubmit: (data: { name: string; icon: string }) => Promise<void>;
}

export function CategoryFormDialog({
  open,
  onClose,
  mode,
  initialData,
  onSubmit,
}: CategoryFormDialogProps) {
  const { t } = useTranslation(["settings", "common"]);
  const id = useId();
  const [name, setName] = useState(initialData?.name ?? "");
  const [icon, setIcon] = useState(initialData?.icon ?? "folder");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialData?.name ?? "");
      setIcon(initialData?.icon ?? "folder");
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    if (trimmedName.length > 50) return;

    setLoading(true);
    try {
      await onSubmit({ name: trimmedName, icon });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setName(initialData?.name ?? "");
    setIcon(initialData?.icon ?? "folder");
    onClose();
  };

  const isValid = name.trim().length > 0 && name.trim().length <= 50;

  return (
    <ConfirmDialog open={open} onClose={handleCancel}>
      <ConfirmDialogTitle>
        {mode === "create"
          ? t("settings:categories.create")
          : t("settings:categories.edit")}
      </ConfirmDialogTitle>
      <ConfirmDialogBody>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label
              htmlFor={id}
              className="text-[13px] font-medium text-primary"
            >
              {t("settings:categories.nameLabel")}
            </label>
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("settings:categories.nameInputPlaceholder")}
              autoFocus
              maxLength={50}
            />
            <p className="text-xs text-secondary">
              {t("settings:categories.charCount", {
                count: name.trim().length,
              })}
            </p>
          </div>
          <div className="space-y-2">
            <div className="text-[13px] font-medium text-primary">
              {t("settings:categories.iconLabel")}
            </div>
            <LucideIconPicker selectedIcon={icon} onSelect={setIcon} />
          </div>
        </div>
      </ConfirmDialogBody>
      <ConfirmDialogActions
        onConfirm={handleSubmit}
        onCancel={handleCancel}
        confirmLabel={
          mode === "create"
            ? t("common:buttons.create")
            : t("common:buttons.save")
        }
        confirmDisabled={!isValid}
        loading={loading}
      />
    </ConfirmDialog>
  );
}
